import type { ReactNode, DragEventHandler, KeyboardEventHandler, MouseEventHandler } from 'react';
import { Download, FileSpreadsheet, Upload } from 'lucide-react';

// Deliberately NOT shared with packages/screens/src/media-import/ (the media-unit
// importer) — that cross-feature import is explicitly banned per
// docs/WEB_ARCHITECTURE.md and the availability-import-ui OpenSpec. This is a
// small, generic-shaped duplicate scoped to this feature only.

export interface UploadDropzoneSectionProps {
  title: string;
  subtitle: string;
  ariaLabel: string;
  selectFileButton: ReactNode;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onDragOver?: DragEventHandler<HTMLDivElement>;
  onDrop?: DragEventHandler<HTMLDivElement>;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  testId?: string;
}

export function UploadDropzoneSection({
  title,
  subtitle,
  ariaLabel,
  selectFileButton,
  onClick,
  onDragOver,
  onDrop,
  onKeyDown,
  testId = 'availability-import-drop-zone',
}: UploadDropzoneSectionProps) {
  return (
    <div
      className="border-2 border-dashed border-border rounded-xl p-12 flex flex-col items-center gap-4 bg-muted/20 hover:bg-muted/30 transition-colors cursor-pointer"
      data-testid={testId}
      role="button"
      aria-label={ariaLabel}
      tabIndex={0}
      onClick={onClick}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onKeyDown={onKeyDown}
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
        <Upload className="h-7 w-7 text-primary" />
      </div>
      <div className="text-center">
        <p className="text-[15px] font-semibold text-foreground">{title}</p>
        <p className="text-[13px] text-muted-foreground mt-1">{subtitle}</p>
      </div>
      {selectFileButton}
    </div>
  );
}

export interface TemplateDownloadHintProps {
  prefixText: string;
  downloadLink: ReactNode;
  hintText?: string;
}

export function TemplateDownloadHint({
  prefixText,
  downloadLink,
  hintText,
}: TemplateDownloadHintProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
        <Download className="h-4 w-4 shrink-0" />
        <span>{prefixText}</span>
        {downloadLink}
      </div>
      {hintText !== undefined ? (
        <p className="text-xs text-muted-foreground/70 pl-6">{hintText}</p>
      ) : null}
    </div>
  );
}

export interface ScopeNoteProps {
  text: string;
}

/** Explains that this import only writes time-boxed availability facts, never unit attributes. */
export function ScopeNote({ text }: ScopeNoteProps) {
  return (
    <div className="rounded-md border border-border bg-muted/30 px-4 py-3 text-xs text-muted-foreground flex items-start gap-2">
      <FileSpreadsheet className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
      <span>{text}</span>
    </div>
  );
}
